import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useDraggable } from '@dnd-kit/core';
import { filterDviewCameras, filterEviewGroups, cameraKey, slotKey, placedSlotKeys } from '../util/cameraCatalog';

/**
 * CameraTreePanel — the tablet Arrange-mode camera source (#86bb5dx62).
 *
 * A CAMERA-ONLY tree (servers → cameras in eView; a flat list in dView) with a
 * 200ms type-ahead filter. Each row is a dnd-kit draggable; drop it on a video
 * tile to assign that camera (VideoViewWs.handleDragEnd → assignCameraToSlot).
 *
 * Deliberately holds NO saved views — per the redesign decision, views live only
 * in the one Views selector (Java's edit-tree "Views branch" is intentionally not
 * ported). Must render INSIDE VideoViewWs's DndContext so drags reach the tiles.
 */
const TreeCameraRow = ({ camera, indent, placed }) => {
  const id = `tree-cam-${cameraKey(camera)}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { type: 'tree-camera', camera },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={'live-edit-tree__cam'
        + (indent ? ' live-edit-tree__cam--indent' : '')
        + (isDragging ? ' live-edit-tree__cam--dragging' : '')
        + (placed ? ' live-edit-tree__cam--placed' : '')}
      data-cam-id={camera.bID}
    >
      <span className="live-edit-tree__num">{String(camera.bID).padStart(2, '0')}</span>
      <span className="live-edit-tree__name">{camera.sName}</span>
      {placed ? (
        <span className="live-edit-tree__check" aria-label="On the canvas">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
        </span>
      ) : (
        <span className="live-edit-tree__grip" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 5h2v2H9V5zm4 0h2v2h-2V5zM9 9h2v2H9V9zm4 0h2v2h-2V9zm-4 4h2v2H9v-2zm4 0h2v2h-2v-2zm-4 4h2v2H9v-2zm4 0h2v2h-2v-2z"/></svg>
        </span>
      )}
    </div>
  );
};

const CameraTreePanel = (props) => {
  const { useEviewLogin, cameras, onCollapse } = props;
  // Cameras already placed on the canvas dim + get a check (they can still be dragged
  // again — the model allows a camera in multiple slots). Keyed by (serial,bID) so the
  // per-slot compositeKey doesn't matter.
  const placed = placedSlotKeys(cameras);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const listRef = useRef(null);
  const focusSerialRef = useRef(null); // the view-server to scroll to on mount (set below)
  // bSerial -> false when collapsed (absent/true = open). Initial state FOCUSES the picker on the view
  // being arranged: expand ONLY the system(s) that have a camera in this view (props.cameras = the loaded
  // arrangement), collapse the rest (David #86bb7phhh). A single-system view — or nothing placed yet —
  // leaves everything at the default-open state.
  const [expanded, setExpanded] = useState(() => {
    if (!useEviewLogin) return {};
    const viewSerials = new Set((cameras || []).filter(c => c && c.fEnable).map(c => c.bSerial));
    if (viewSerials.size === 0) return {};
    const groups = filterEviewGroups(props, '');
    if (groups.length <= 1) return {};
    const init = {};
    groups.forEach(g => { init[g.server.bSerial] = viewSerials.has(g.server.bSerial); });
    const first = groups.find(g => viewSerials.has(g.server.bSerial)); // scroll here on mount
    focusSerialRef.current = first ? first.server.bSerial : null;
    return init;
  });
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // On entering Arrange, SCROLL the picker so the auto-expanded (view) system sits at the top — else it's
  // open but off-screen (e.g. "O's Flagstaff CC" is far down the list). Multi-system → the first one in
  // list order. David #86bb7phhh.
  useEffect(() => {
    const s = focusSerialRef.current;
    if (s == null || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-server-serial="${s}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(v), 200); // Java edit-tree parity (L-C3)
  };
  const clearSearch = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSearch('');
    setDebounced('');
  };
  const toggleServer = (bSerial) =>
    setExpanded(p => ({ ...p, [bSerial]: p[bSerial] === false ? true : false }));

  // Expand / collapse EVERY system at once (David #86bb7phhh) — lets the user see all available systems
  // at a glance without scrolling. `expanded[s] === false` means collapsed; {} = all default-open.
  const allSerials = useEviewLogin ? filterEviewGroups(props, '').map(g => g.server.bSerial) : [];
  const allCollapsed = allSerials.length > 0 && allSerials.every(s => expanded[s] === false);
  const toggleAll = () =>
    setExpanded(allCollapsed ? {} : allSerials.reduce((m, s) => { m[s] = false; return m; }, {}));

  const searching = !!debounced.trim();
  let body;

  if (useEviewLogin) {
    const groups = filterEviewGroups(props, debounced);
    body = groups.length === 0
      ? <div className="live-edit-tree__empty">{searching ? 'No cameras match' : 'No cameras'}</div>
      : groups.map(({ server, cameras }) => {
          const isOpen = searching || expanded[server.bSerial] !== false;
          return (
            <div key={server.bSerial} className="live-edit-tree__group" data-server-serial={server.bSerial}>
              <button
                type="button"
                className="live-edit-tree__server"
                onClick={() => toggleServer(server.bSerial)}
              >
                <span className="live-edit-tree__caret">{isOpen ? '▾' : '▸'}</span>
                <span className="live-edit-tree__server-name">{server.sName}</span>
                <span className="live-edit-tree__count">{cameras.length} cam{cameras.length === 1 ? '' : 's'}</span>
              </button>
              {isOpen && cameras.map(c => <TreeCameraRow key={cameraKey(c)} camera={c} indent placed={placed.has(slotKey(c))} />)}
            </div>
          );
        });
  } else {
    const cams = filterDviewCameras(props, debounced);
    body = cams.length === 0
      ? <div className="live-edit-tree__empty">{searching ? 'No cameras match' : 'No cameras'}</div>
      : cams.map(c => <TreeCameraRow key={cameraKey(c)} camera={c} placed={placed.has(slotKey(c))} />);
  }

  return (
    <aside className="live-edit-tree" aria-label="Camera list">
      <div className="live-edit-tree__head">
        <span className="live-edit-tree__head-title">Cameras</span>
        {onCollapse && (
          <button type="button" className="live-edit-tree__collapse" onClick={onCollapse} aria-label="Collapse camera list" title="Collapse panel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14.6 5.6 16 7l-5 5 5 5-1.4 1.4L8.2 12z"/></svg>
          </button>
        )}
      </div>
      <div className="live-edit-tree__search">
        <span className="live-edit-tree__search-ic" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 10-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1114 9.5 4.5 4.5 0 019.5 14z"/></svg>
        </span>
        <input
          type="text"
          className="live-edit-tree__search-input"
          placeholder="Search cameras…"
          value={search}
          onChange={onSearch}
        />
        {search && (
          <button type="button" className="live-edit-tree__search-clear" onClick={clearSearch} aria-label="Clear search">×</button>
        )}
      </div>
      {allSerials.length > 1 && (
        <div className="live-edit-tree__listctl">
          <button type="button" className="live-edit-tree__toggleall" onClick={toggleAll}
            aria-label={allCollapsed ? 'Expand all systems' : 'Collapse all systems'}>
            {allCollapsed ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5.83 15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9 12 5.83zm0 12.34L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15 12 18.17z"/></svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M7.41 18.59 8.83 20 12 16.83 15.17 20l1.42-1.41L12 14l-4.59 4.59zm9.18-13.18L15.17 4 12 7.17 8.83 4 7.41 5.41 12 10l4.59-4.59z"/></svg>
            )}
            <span>{allCollapsed ? 'Expand all' : 'Collapse all'}</span>
          </button>
        </div>
      )}
      <div className="live-edit-tree__list" ref={listRef}>{body}</div>
      <div className="live-edit-tree__hint">Drag a camera onto a tile</div>
    </aside>
  );
};

const mapStateToProps = (state) => {
  const { cameras, bNumCams, allCameras, camerasByServer, authServers } = state.server;
  const { originalCameras } = state.view;
  const { useEviewLogin } = state.auth;
  return {
    cameras,
    originalCameras,
    bNumCams: bNumCams || 64,
    useEviewLogin,
    allCameras: allCameras || [],
    camerasByServer: camerasByServer || {},
    authServers: authServers || [],
  };
};

export { CameraTreePanel };
export default connect(mapStateToProps)(CameraTreePanel);
